/**
 * THE STORYLINE — the lede, built only from governed labels and binding
 * placeholders (spec §3.2, `docs/specs/ask-mbb-storyline-v1.md`). S1 composes
 * only the `kpi` shape's fact lede; every other declared kind returns `null`
 * until its own slice lands (S2 ranked_bars, S3 bridge/decision_table, S4
 * status_board).
 *
 * NO ARITHMETIC. NO NEW NUMBERS. Every `{figure}` segment names a binding
 * placeholder; the value, its display string and its provenance badge stay on
 * the binding, exactly as `declared-exhibit.ts` requires of exhibit figures.
 * A figure with no matching binding is skipped, never invented.
 */
import type { AskHelixNumericBindingDto } from '../../ask-helix';
import type {
  AskDeclaredExhibitBody,
  AskDeclaredExhibitDto,
  AskExhibitFigure,
} from './declared-exhibit';
import { askExhibitPlaceholders } from './declared-exhibit-geometry';
import type { AskNarrativeLens } from '../narrative/narrative-lens';
import { storyFromPlan } from '../narrative/narrative-lenses';
import {
  bridgeChangeFact,
  completeNarrativePlan,
  kpiFigureFact,
  rankLeaderFact,
  type AskNarrationRank,
  type AskPlanFact,
} from '../narrative/narrative-plan';
import {
  voiceFigureFact,
  voiceGapFact,
  voiceRankNoun,
  type AskResolvedVoice,
} from '../narrative/voice-facts';

/**
 * What the server knows beyond the exhibit (narration slice 1): each output's governed measure
 * name, and the engine's rank receipts. Server-only: it shapes words, never the wire.
 */
export type AskStoryContext = Readonly<{
  measure: (outputId: string) => string | null;
  ranks: readonly AskNarrationRank[];
  /** Slice 1b: the output's measure voice (from its family ref), or null → today's form. */
  voice?: (outputId: string) => AskResolvedVoice | null;
  /** The output's grouping dimensions, from the public ledger. */
  groupBy?: (outputId: string) => readonly string[];
}>;

const NO_CONTEXT: AskStoryContext = { measure: () => null, ranks: [] };

type BridgeExhibit = Extract<AskDeclaredExhibitBody, { kind: 'bridge' }>;
type RankedExhibit = Extract<AskDeclaredExhibitBody, { kind: 'ranked_bars' }>;

/** A same-period gap in the actual side's voice when both sides are typed; else the S1 fact. */
function bridgeFact(
  exhibit: BridgeExhibit,
  bindings: readonly AskHelixNumericBindingDto[],
  displayLabel: (raw: string) => string,
  context: AskStoryContext
): AskPlanFact | null {
  const at = (placeholder: string) =>
    bindings.find((binding) => binding.placeholder === placeholder);
  const [change, from, to] = [exhibit.change.figure, exhibit.from.figure, exhibit.to.figure].map(
    at
  );
  const voiceOf = (binding: AskHelixNumericBindingDto | undefined) =>
    binding?.outputId ? (context.voice?.(binding.outputId) ?? null) : null;
  const voiced = change
    ? voiceGapFact(change, { from: voiceOf(from), to: voiceOf(to) }, displayLabel)
    : null;
  return voiced ?? bridgeChangeFact(exhibit, bindings, displayLabel);
}

/** The leader fact, named with the family's rank noun for the grouping dimension when voiced. */
function rankFact(
  exhibit: RankedExhibit,
  bindings: readonly AskHelixNumericBindingDto[],
  displayLabel: (raw: string) => string,
  context: AskStoryContext
): AskPlanFact | null {
  const fact = rankLeaderFact(exhibit, bindings, context, displayLabel);
  const leader = bindings.find((binding) => binding.placeholder === fact?.figures.leader);
  const outputId = leader?.outputId;
  if (!fact || !outputId) return fact;
  const [dimension] = context.groupBy?.(outputId) ?? [];
  const end = fact.labels.end === 'bottom' ? 'bottom' : 'top';
  const rankNoun = voiceRankNoun(context.voice?.(outputId) ?? null, dimension, end);
  return rankNoun ? { ...fact, labels: { ...fact.labels, rankNoun } } : fact;
}

/**
 * One lede segment: governed literal text, or a binding placeholder to resolve. `magnitude`
 * (narration slice 1b, lead guard (B) 09:40): render the SAME binding's absolute display, because
 * the direction words beside it already carry the sign ("$1M behind plan", never "-$1M behind").
 */
export type AskStorySegment =
  Readonly<{ text: string }> | Readonly<{ figure: string; magnitude?: true }>;

/**
 * Story v2 (narration S1, review Option D): the lede, supporting drivers, the
 * domain lens's mandatory qualifications (reviewed sentences, never a number)
 * and at most two follow-ups. Every figure is a binding placeholder.
 */
export type AskStoryline = Readonly<{
  lede: readonly AskStorySegment[];
  drivers?: readonly (readonly AskStorySegment[])[];
  qualifications?: readonly string[];
  followUps?: readonly Readonly<{ id: string; label: string }>[];
  /** The advisor's recommended next step (founder 16:55): an allowed lens action only. */
  recommendation?: Readonly<{ text: readonly AskStorySegment[]; why?: string }>;
}>;

/**
 * Composes the storyline for a declared exhibit through the narrative plan
 * and the shared microgrammars. `null` when no reviewed grammar covers the
 * exhibit's governing fact yet, or when none of its figures resolved.
 */
export function composeAskStory(
  declared: AskDeclaredExhibitDto,
  bindings: readonly AskHelixNumericBindingDto[],
  lens: AskNarrativeLens | null = null,
  displayLabel: (raw: string) => string = (raw) => raw,
  context: AskStoryContext = NO_CONTEXT
): AskStoryline | null {
  const told = (fact: AskPlanFact | null) =>
    fact === null ? null : storyFromPlan(completeNarrativePlan(fact, [], lens), lens);
  const exhibit = declared.exhibit;
  if (exhibit.kind === 'bridge') return told(bridgeFact(exhibit, bindings, displayLabel, context));
  if (exhibit.kind === 'ranked_bars')
    return told(rankFact(exhibit, bindings, displayLabel, context));
  if (exhibit.kind !== 'kpi') return null;
  const byPlaceholder = new Map(bindings.map((binding) => [binding.placeholder, binding]));
  const [only] = exhibit.figures;
  const binding = only ? byPlaceholder.get(only.figure) : undefined;
  if (exhibit.figures.length === 1 && binding) {
    // Narration slice 1 / 1b: a single figure is stated answer first, in its family's voice.
    const voice = binding.outputId ? (context.voice?.(binding.outputId) ?? null) : null;
    const voiced = voice ? voiceFigureFact(binding, voice, displayLabel) : null;
    const measure = binding.outputId ? context.measure(binding.outputId) : null;
    return told(
      voiced ?? kpiFigureFact(binding.displayLabel ?? only.label, binding, displayLabel, measure)
    );
  }
  const sentences = exhibit.figures
    .map((figure) => kpiSentence(figure, byPlaceholder.get(figure.figure)))
    .filter((sentence): sentence is readonly AskStorySegment[] => sentence !== null);
  return sentences.length === 0 ? null : { lede: joinSentences(sentences) };
}

/**
 * "{label} for {scope}, {period}." — text only. The KPI card draws the figure,
 * and each figure appears ONCE, on the exhibit (founder rule 2026-09-24 03:48;
 * Codex HOLD @ 06aa9a81 on #8842). No period clause when the timeframe has no label.
 */
function kpiSentence(
  figure: AskExhibitFigure,
  binding: AskHelixNumericBindingDto | undefined
): readonly AskStorySegment[] | null {
  if (!binding) return null;
  const label = binding.displayLabel ?? figure.label;
  const period = binding.timeframe.label ? `, ${binding.timeframe.label}` : '';
  // No "X for X" when the label is the scope itself (review note @ 8161b846).
  const scope = binding.scope.label === label ? '' : ` for ${binding.scope.label}`;
  return [{ text: `${label}${scope}${period}.` }];
}

/**
 * Everything wrong with a story for its exhibit, named; empty is the only
 * publishable state. The lede may cite the governing FACT's exhibit-owned figures
 * (at most two: a change and its endpoint), as a slide headline cites the number
 * its chart shows (lead rulings 2026-09-26 13:56, 15:02). A third, or any owned
 * figure in a driver, would restate the exhibit as text and is rejected.
 */
const MAX_LEDE_OWNED_FIGURES = 2;

export function askStoryProblems(
  story: AskStoryline,
  exhibit: AskDeclaredExhibitBody
): readonly string[] {
  const owned = new Set(askExhibitPlaceholders(exhibit));
  const ownedIn = (segments: readonly AskStorySegment[]): string[] =>
    segments.flatMap((segment) =>
      'figure' in segment && owned.has(segment.figure) ? [segment.figure] : []
    );
  const problems: string[] = [];
  const cited = ownedIn(story.lede);
  if (cited.length > MAX_LEDE_OWNED_FIGURES) {
    problems.push(
      `lede cites ${cited.length} exhibit-owned figures; ` +
        `only the governing fact (at most ${MAX_LEDE_OWNED_FIGURES}) may be cited`
    );
  }
  for (const figure of (story.drivers ?? []).flatMap(ownedIn)) {
    problems.push(`exhibit-owned figure ${figure} is in a driver`);
  }
  return problems;
}

function joinSentences(
  sentences: readonly (readonly AskStorySegment[])[]
): readonly AskStorySegment[] {
  return sentences.flatMap((sentence, index) =>
    index === 0 ? sentence : [{ text: ' ' } as const, ...sentence]
  );
}
